// ─── Nearby map: Snap Map-style store pins (v3.0) ─────────────────────────
// Leaflet + OpenStreetMap (free, no API key). Pins are STORES, not deals:
// circular monogram badge + "STORE · N deals" label, Snap place-label
// energy. The store with the deepest discount gets a "Top Pick" tag.
// Tapping a pin opens a bottom sheet: deal count + top 3 preview +
// "View all deals" deep-links to the Deals tab filtered to that store.
//
// ONLY verified coordinates — never invented. To add a pin, append one
// line to EXTRA_PINS: { storeId, label, lat, lng }.
import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { T, BottomSheet, buzzLight } from "./components";
import { WEEKLY_DEALS, WEEKLY_STORE_LOCATIONS } from "./seedData";
import { BRANDS } from "./brands";
import { WeeklyRow } from "./weekly";
import { IconPin } from "./icons";

const CALGARY = [51.0447, -114.0719];

// Verified 2026-10-14/17 — append new verified pins here, one line each.
const EXTRA_PINS = [
  { storeId: "nofrills", label: "Temple NE", lat: 51.085186, lng: -113.955897 },
  { storeId: "sobeys", label: "Country Hills NE", lat: 51.15512326, lng: -114.05742323 },
  { storeId: "safeway", label: "Crowfoot NW", lat: 51.12533242, lng: -114.20064698 },
  { storeId: "coop", label: "Brentwood NW", lat: 51.08822231, lng: -114.13091908 },
  { storeId: "saveonfoods", label: "Panatella Hill NW", lat: 51.16732567, lng: -114.08930794 },
  { storeId: "walmart", label: "Sage Hill NW", lat: 51.16981023, lng: -114.1425555 },
  { storeId: "freshco", label: "Symons Valley NW", lat: 51.17538214, lng: -114.10945061 },
];

const PINS = [
  ...Object.entries(WEEKLY_STORE_LOCATIONS).flatMap(([storeId, spots]) =>
    spots.map((s) => ({ storeId, label: s.label, lat: s.lat, lng: s.lng }))
  ),
  ...EXTRA_PINS,
];

const isLive = (d) => new Date(d.validTo + "T23:59:59").getTime() >= Date.now();
const pctOff = (d) =>
  d.regularPrice != null && d.price != null && d.regularPrice > d.price
    ? (d.regularPrice - d.price) / d.regularPrice
    : 0;

// Per-store: live deal count + deepest discount + deals sorted best-first.
function storeStats() {
  const byId = {};
  for (const d of WEEKLY_DEALS) {
    if (!isLive(d)) continue;
    const s = byId[d.storeId] || (byId[d.storeId] = { count: 0, bestPct: 0, deals: [] });
    s.count += 1;
    s.bestPct = Math.max(s.bestPct, pctOff(d));
    s.deals.push(d);
  }
  for (const id of Object.keys(byId)) {
    byId[id].deals.sort(
      (a, b) => pctOff(b) - pctOff(a) || (a.price ?? Infinity) - (b.price ?? Infinity)
    );
  }
  return byId;
}

const brandFor = (storeId) => BRANDS.find((b) => b.id === storeId);

// Leaflet recalculates its size on mount — critical when the map lives in a
// tab that mounts/unmounts, otherwise tiles render half-grey.
function FixSize() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 120);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

// Snap place-label pin: circular monogram badge + "STORE · N deals".
// Top Pick store gets the black tag, Snap-style.
function storeIcon(brand, count, isTop, i) {
  const label = `${brand.label.toUpperCase()} · ${count}`;
  return L.divIcon({
    className: "gd-store-pin",
    html:
      `<div style="display:flex;flex-direction:column;align-items:center;` +
      `transform:translate(-50%,-100%);` +
      `animation:pinDrop .55s cubic-bezier(.22,1,.36,1) both;` +
      `animation-delay:${Math.min(i, 10) * 60}ms;">` +
      (isTop
        ? `<div style="background:#141210;color:#fff;` +
          `font:800 10px 'DM Sans',sans-serif;letter-spacing:.8px;` +
          `padding:3px 9px;border-radius:20px;margin-bottom:5px;` +
          `box-shadow:0 1px 6px rgba(0,0,0,.25);">TOP PICK</div>`
        : "") +
      `<div style="display:flex;align-items:center;gap:7px;background:#fff;` +
      `border:1.5px solid #E8E2D8;border-radius:999px;padding:5px 12px 5px 5px;` +
      `box-shadow:0 2px 12px rgba(0,0,0,.18);white-space:nowrap;">` +
      `<span style="width:30px;height:30px;border-radius:50%;` +
      `background:${brand.color};color:#fff;` +
      `font:900 12px 'DM Sans',sans-serif;display:flex;` +
      `align-items:center;justify-content:center;">${brand.mono}</span>` +
      `<span style="font:700 12.5px 'DM Sans',sans-serif;color:#1C1915;` +
      `font-variant-numeric:tabular-nums;">${label}</span>` +
      `</div></div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

// "Me now" marker: a playful illustrated avatar, not a blue dot.
function meIcon() {
  return L.divIcon({
    className: "gd-me-pin",
    html:
      `<div style="display:flex;flex-direction:column;align-items:center;` +
      `transform:translate(-50%,-100%);">` +
      `<div style="width:46px;height:46px;border-radius:50%;background:#1F7A4D;` +
      `border:3px solid #fff;box-shadow:0 2px 12px rgba(0,0,0,.3);` +
      `display:flex;align-items:center;justify-content:center;` +
      `animation:meBob 2.4s ease-in-out infinite;">` +
      `<svg width="26" height="26" viewBox="0 0 26 26">` +
      `<circle cx="9" cy="11" r="2.2" fill="#fff"/>` +
      `<circle cx="17" cy="11" r="2.2" fill="#fff"/>` +
      `<path d="M8.5 16.5q4.5 4 9 0" stroke="#fff" stroke-width="2.2" ` +
      `stroke-linecap="round" fill="none"/></svg>` +
      `</div>` +
      `<div style="margin-top:5px;background:#141210;color:#fff;` +
      `font:800 10.5px 'DM Sans',sans-serif;padding:3px 10px;` +
      `border-radius:20px;letter-spacing:.6px;">YOU</div>` +
      `</div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

export function StoreMap({
  loc, locStatus, onLocate, locBusy,
  savedItems = [], onSaveItem = () => {}, onViewStore = () => {},
}) {
  const [activePin, setActivePin] = useState(null);
  const stats = useMemo(storeStats, []);
  const savedSet = useMemo(
    () => new Set((savedItems || []).map((i) => (i.text || "").toLowerCase())),
    [savedItems]
  );

  const pins = useMemo(
    () =>
      PINS.map((p) => ({ ...p, brand: brandFor(p.storeId) })).filter(
        (p) => p.brand && stats[p.storeId]
      ),
    [stats]
  );
  // Top Pick = the pinned store with the deepest single discount.
  const topPickId = useMemo(() => {
    let best = null;
    for (const p of pins) {
      const s = stats[p.storeId];
      if (!s || s.bestPct <= 0) continue;
      if (!best || s.bestPct > stats[best].bestPct) best = p.storeId;
    }
    return best;
  }, [pins, stats]);

  const totalDeals = useMemo(
    () => Object.values(stats).reduce((n, s) => n + s.count, 0),
    [stats]
  );

  const activeStats = activePin ? stats[activePin.storeId] : null;
  const activeBrand = activePin ? brandFor(activePin.storeId) : null;

  return (
    <div>
      <div style={styles.tabHead}>
        <div style={styles.tabTitle}>Nearby.</div>
        <div style={styles.tabSub} className="tnum">
          {pins.length} stores · {totalDeals} deals this week
        </div>
      </div>

      {locStatus === "unasked" && (
        <button onClick={onLocate} disabled={locBusy} style={styles.optIn}>
          <span style={styles.optInIcon}>
            <IconPin size={20} />
          </span>
          <span style={{ flex: 1, textAlign: "left" }}>
            <span style={styles.optInTitle}>
              {locBusy ? "Finding you…" : "Show deals near me"}
            </span>
            <span style={styles.optInSub}>
              One tap — we'll drop a pin on you, not a blue dot.
            </span>
          </span>
        </button>
      )}
      {locStatus === "denied" && (
        <div style={styles.optOut}>
          Location's off — showing all of Calgary. Turn it on in Profile
          to get the personal pin.
        </div>
      )}

      <div style={styles.mapWrap}>
        <MapContainer
          center={loc ? [loc.lat, loc.lng] : CALGARY}
          zoom={loc ? 12 : 11}
          style={{ height: "100%", width: "100%", borderRadius: 18 }}
          scrollWheelZoom={true}
          attributionControl={true}
        >
          <FixSize />
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {pins.map((p, i) => (
            <Marker
              key={`${p.storeId}-${p.label}`}
              position={[p.lat, p.lng]}
              icon={storeIcon(
                p.brand,
                stats[p.storeId].count,
                p.storeId === topPickId,
                i
              )}
              eventHandlers={{
                click: () => {
                  buzzLight();
                  setActivePin(p);
                },
              }}
            />
          ))}
          {loc && (
            <Marker position={[loc.lat, loc.lng]} icon={meIcon()} interactive={false} />
          )}
        </MapContainer>
      </div>
      <div style={styles.mapNote}>
        Tap a store to peek at its best deals.
      </div>

      <BottomSheet
        open={!!activePin}
        onClose={() => setActivePin(null)}
        title={activeBrand ? activeBrand.label : ""}
        maxHeight="70vh"
      >
        {activePin && activeStats && (
          <div>
            <div style={styles.sheetHead}>
              <span
                style={{
                  ...styles.sheetMono,
                  background: activeBrand.color,
                }}
              >
                {activeBrand.mono}
              </span>
              <div>
                <div style={styles.sheetStore}>{activeBrand.label}</div>
                <div style={styles.sheetCount} className="tnum">
                  {activeStats.count} deals this week · {activePin.label}
                </div>
              </div>
            </div>
            {activeStats.deals.slice(0, 3).map((d) => (
              <WeeklyRow
                key={d.id}
                deal={d}
                best={false}
                expired={false}
                km={null}
                saved={savedSet.has((d.item || "").toLowerCase())}
                onSave={() => {
                  buzzLight();
                  onSaveItem(d);
                }}
              />
            ))}
            <button
              onClick={() => {
                setActivePin(null);
                onViewStore(activePin.storeId);
              }}
              style={styles.sheetCta}
            >
              View all {activeStats.count} deals
            </button>
          </div>
        )}
      </BottomSheet>
    </div>
  );
}

const styles = {
  tabHead: { marginBottom: 14 },
  tabTitle: { fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 28, letterSpacing: -0.5 },
  tabSub: { fontSize: 12.5, color: T.textSub, marginTop: 4 },
  optIn: {
    display: "flex", gap: 12, alignItems: "center", width: "100%",
    background: "#EAF5EE", border: "1.5px solid #BFE3CC", borderRadius: 16,
    padding: "13px 14px", marginBottom: 12, cursor: "pointer",
    fontFamily: "inherit", textAlign: "left",
  },
  optInIcon: {
    width: 40, height: 40, borderRadius: 13, background: "#1F7A4D", color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  optInTitle: { display: "block", fontSize: 15, fontWeight: 800, color: "#14532D", letterSpacing: -0.2 },
  optInSub: { display: "block", fontSize: 12.5, color: "#2C6B45", marginTop: 2 },
  optOut: {
    fontSize: 12.5, color: T.textSub, background: T.bgCard,
    border: `1px solid ${T.border}`, borderRadius: 12,
    padding: "10px 13px", marginBottom: 12, lineHeight: 1.5,
  },
  mapWrap: {
    height: "58vh", minHeight: 380, borderRadius: 18, overflow: "hidden",
    border: `1px solid ${T.border}`, boxShadow: T.shadow,
  },
  mapNote: {
    fontSize: 12, color: T.textMuted, textAlign: "center",
    marginTop: 10, marginBottom: 4,
  },
  sheetHead: { display: "flex", gap: 12, alignItems: "center", marginBottom: 14 },
  sheetMono: {
    width: 52, height: 52, borderRadius: 17, color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 17, fontWeight: 900, flexShrink: 0,
  },
  sheetStore: { fontSize: 19, fontWeight: 800, letterSpacing: -0.3 },
  sheetCount: { fontSize: 12.5, color: T.textSub, marginTop: 2 },
  sheetCta: {
    width: "100%", padding: 16, borderRadius: 999, border: "none", cursor: "pointer",
    background: "#141210", color: "#fff", fontSize: 15.5, fontWeight: 800,
    letterSpacing: -0.2, fontFamily: "inherit", marginTop: 14,
  },
};
